import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";

function ResultRow({ p, active, onPick, onHover, i }) {
  return (
    <button type="button" onClick={() => onPick(p)} onMouseEnter={() => onHover(i)}
      className={`w-full flex items-center gap-4 p-3 rounded-2xl text-left transition-colors ${active ? "bg-black/[0.05]" : ""}`}
      data-testid={`search-result-${p.id}`}>
      <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0" style={{ background: "linear-gradient(180deg, #F7F2E7, #EFE9D9)" }}>
        <img src={p.image} alt={p.name} className="w-full h-full object-contain p-1 mix-blend-multiply" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="num-badge text-[9px]">{p.brand} · {p.category}</div>
        <div className="font-display text-lg leading-tight truncate" style={{ color: "var(--ink)" }}>{p.name}</div>
      </div>
      <div className="text-sm font-semibold whitespace-nowrap" style={{ color: "var(--ink)" }}>₹{p.price?.toLocaleString("en-IN")}</div>
      <ArrowRight className={`w-4 h-4 transition-opacity ${active ? "opacity-100" : "opacity-0"}`} style={{ color: "#8A6A0F" }} />
    </button>
  );
}

export default function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 50);
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    let cancelled = false;
    const t = setTimeout(() => {
      api.get(`/products?q=${encodeURIComponent(term)}&limit=6`)
        .then((r) => { if (!cancelled) { setResults(r.data); setActive(0); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const close = () => { setQ(""); setResults([]); onClose(); };
  const pick = (p) => { navigate(`/product/${p.id}`); close(); };
  const viewAll = () => { if (q.trim()) { navigate(`/shop?q=${encodeURIComponent(q.trim())}`); close(); } };

  const onKey = (e) => {
    if (e.key === "Escape") close();
    else if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); results[active] ? pick(results[active]) : viewAll(); }
  };

  if (!open) return null;
  const term = q.trim();

  return createPortal(
    <div className="fixed inset-0 z-[100] flex justify-center px-4 pt-[10vh] md:pt-[14vh] animate-[fadeIn_.2s_ease]" data-testid="search-overlay" onKeyDown={onKey}>
      <div className="absolute inset-0" style={{ background: "rgba(247,242,231,0.96)", backdropFilter: "blur(18px)" }} onClick={close} data-testid="search-backdrop"></div>
      <div className="relative w-full max-w-3xl">
        <div className="flex items-center gap-4 pb-4 border-b-2" style={{ borderColor: "#8A6A0F" }}>
          <Search className="w-6 h-6 shrink-0" style={{ color: "#8A6A0F" }} />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search speakers, receivers, projectors…"
            className="!bg-transparent !border-0 !shadow-none !p-0 !ring-0 flex-1 font-display !text-2xl md:!text-4xl outline-none" data-testid="search-input" />
          {loading && <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#8A6A0F" }} />}
          <button type="button" onClick={close} aria-label="Close search" className="p-2 rounded-full hover:bg-black/5" data-testid="search-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-4 max-h-[60vh] overflow-y-auto" data-testid="search-results">
          {term.length < 2 && <p className="text-sm py-6" style={{ color: "rgba(15,12,8,0.5)" }}>Type at least 2 letters to see products.</p>}
          {term.length >= 2 && !loading && results.length === 0 && <p className="text-sm py-6" style={{ color: "rgba(15,12,8,0.5)" }} data-testid="search-empty">No products match "{term}".</p>}
          {results.map((p, i) => <ResultRow key={p.id} p={p} i={i} active={i === active} onPick={pick} onHover={setActive} />)}
          {results.length > 0 && (
            <button type="button" onClick={viewAll} className="mt-3 text-xs uppercase tracking-[0.2em] font-semibold inline-flex items-center gap-2 brass-text" data-testid="search-view-all">
              View all results for "{term}" <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="hidden md:block mt-6 text-[10px] tracking-[0.2em] uppercase" style={{ color: "rgba(15,12,8,0.4)", fontFamily: "'JetBrains Mono', monospace" }}>↑ ↓ to move · Enter to open · Esc to close</div>
      </div>
    </div>,
    document.body
  );
}
